import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "@/components/Reveal";
import MaskedLines from "@/components/MaskedLines";
import { ArrowUpRight } from "lucide-react";
import { COLLECTION } from "@/constants/testIds";
import { PAYHIP_STORE_URL } from "@/lib/config";
import useProducts from "@/hooks/useProducts";

/**
 * Product card with spotlight hover treatment.
 * The cursor drives a radial-gradient reveal on the image.
 */
function ProductCard({ p, index }) {
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [hover, setHover] = useState(false);
  const wrapRef = useRef(null);

  const handleMove = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPos({ x, y });
  };

  // Clipped frame variants — alternate for asymmetric editorial feel
  const clipVariants = [
    "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
    "polygon(6% 0, 100% 0, 94% 100%, 0 100%)",
    "polygon(0 0, 94% 0, 100% 100%, 6% 100%)",
  ];
  const clip = clipVariants[index % clipVariants.length];

  return (
    <Reveal delay={index * 0.06}>
      <a
        href={p.url}
        target="_blank"
        rel="noreferrer noopener"
        data-testid={COLLECTION.card(p.slug)}
        className="group block"
        onMouseMove={handleMove}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div
          ref={wrapRef}
          className="relative w-full aspect-[4/5] overflow-hidden bg-[#0f1216]"
          style={{ clipPath: clip, WebkitClipPath: clip }}
        >
          {/* Index number floating */}
          <span className="absolute top-4 left-4 z-20 text-[#e8ecef]/80 text-[10px] tracking-[0.4em] font-mono">
            {String(index + 1).padStart(2, "0")}
          </span>

          {p.img ? (
            <>
              {/* Dimmed base image */}
              <img
                src={p.img}
                alt={p.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.05]"
                draggable={false}
                loading="lazy"
                style={{ filter: "brightness(0.72) saturate(0.9)" }}
              />
              {/* Spotlight image — revealed only around cursor */}
              <img
                src={p.img}
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-500"
                draggable={false}
                loading="lazy"
                style={{
                  opacity: hover ? 1 : 0,
                  WebkitMaskImage: `radial-gradient(circle 200px at ${pos.x}% ${pos.y}%, black 0%, black 40%, transparent 75%)`,
                  maskImage: `radial-gradient(circle 200px at ${pos.x}% ${pos.y}%, black 0%, black 40%, transparent 75%)`,
                }}
              />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#3a444a] text-[10px] tracking-[0.3em] uppercase">
              {p.name}
            </div>
          )}

          {/* Bottom color blend */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#101820] via-[#101820]/60 to-transparent" />

          {/* Tags */}
          {p.tag && (
            <div className="absolute top-4 right-4 z-20 bg-[#0a0a0a]/70 backdrop-blur-md px-3 py-1 text-[10px] tracking-[0.28em] uppercase text-[#e8ecef] border border-[#c9d3d8]/10">
              {p.tag}
            </div>
          )}
          {!p.in_stock && (
            <div className="absolute bottom-4 right-4 z-20 bg-[#e8ecef] text-[#0a0a0a] px-3 py-1 text-[10px] tracking-[0.28em] uppercase">
              Sold Out
            </div>
          )}

          {/* Hover CTA slide-in */}
          <div className="absolute bottom-4 left-4 z-20 overflow-hidden">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#e8ecef] translate-y-full group-hover:translate-y-0 transition-transform duration-[700ms] ease-[cubic-bezier(0.22,1,0.36,1)]">
              View
              <ArrowUpRight size={12} />
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-serif text-2xl md:text-3xl leading-tight tracking-tight text-[#e8ecef]">
              {p.name}
            </h3>
            {p.description && (
              <p className="mt-2 text-sm text-[#8a9498] leading-relaxed max-w-[36ch] font-light">
                {p.description}
              </p>
            )}
          </div>
          {p.price && (
            <span className="text-[13px] tracking-[0.15em] whitespace-nowrap text-[#8a9498] mt-1">
              {p.price}
            </span>
          )}
        </div>
        <div
          data-testid={COLLECTION.shopNow(p.slug)}
          className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase border-b border-[#e8ecef]/60 pb-1 group-hover:gap-3 group-hover:border-[#e8ecef] transition-all duration-500"
        >
          {p.in_stock ? "Shop on Payhip" : "View on Payhip"}
          <ArrowUpRight size={12} />
        </div>
      </a>
    </Reveal>
  );
}

export default function Collection() {
  const { products } = useProducts();
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);

  return (
    <section
      ref={sectionRef}
      id="collection"
      data-testid={COLLECTION.root}
      className="relative text-[#e8ecef] py-32 md:py-48 overflow-hidden"
      style={{
        backgroundImage:
          "linear-gradient(to bottom, #0f2231 0%, #101820 15%, #101820 85%, #142e25 100%)",
      }}
    >
      {/* Faint parallax number in background */}
      <motion.div
        aria-hidden
        style={{ y: bgY }}
        className="pointer-events-none absolute -top-10 -right-6 md:-top-14 md:-right-10 font-serif text-[#e8ecef]/[0.04] text-[220px] md:text-[380px] leading-none tracking-tighter select-none"
      >
        03
      </motion.div>

      <div className="relative max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-20 md:mb-28">
          <div>
            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <span className="block h-px w-14 md:w-24 bg-[#c9d3d8]/40" />
                <p className="text-[11px] tracking-[0.5em] uppercase text-[#c9d3d8]">
                  Chapter 03 — The Collection
                </p>
              </div>
            </Reveal>
            <MaskedLines
              as="h2"
              whileInView
              lines={["Objects made", "to live with."]}
              stagger={0.1}
              duration={1.05}
              className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight font-light"
            />
            <Reveal delay={0.35}>
              <p className="mt-8 text-[#8a9498] text-sm tracking-[0.2em] uppercase font-light">
                Shipped through payhip.com/JUniverse
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <a
              href={PAYHIP_STORE_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={COLLECTION.viewAll}
              className="group relative inline-flex items-center gap-3 border border-[#e8ecef]/50 text-[#e8ecef] px-8 py-4 text-[11px] tracking-[0.3em] uppercase overflow-hidden hover:text-[#0a0a0a] transition-colors duration-500"
            >
              <span className="relative z-10">View Full Collection</span>
              <ArrowUpRight
                size={14}
                className="relative z-10 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
              <span className="absolute inset-0 bg-[#e8ecef] translate-y-full group-hover:translate-y-0 transition-transform duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]" />
            </a>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-14">
          {products.map((p, i) => (
            <ProductCard key={p.slug} p={p} index={i} />
          ))}
        </div>

        {/* Payhip storefront CTA */}
        <Reveal delay={0.1}>
          <div className="mt-28 md:mt-40 border-t border-[#1a2028]/70 pt-20 md:pt-28 flex flex-col items-center text-center">
            <p className="text-[11px] tracking-[0.5em] uppercase text-[#c9d3d8]/70 mb-8">
              The Full Storefront
            </p>
            <h3 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
              Every drop, restock, and new release lives at<br />
              <span className="font-light text-[#8a9498]">
                payhip.com/JUniverse
              </span>
            </h3>
            <a
              href={PAYHIP_STORE_URL}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="collection-payhip-cta"
              className="group relative mt-12 inline-flex items-center gap-3 bg-[#e8ecef] text-[#0a0a0a] px-10 py-4 text-[12px] tracking-[0.32em] uppercase overflow-hidden"
            >
              <span className="relative z-10">Enter The Storefront</span>
              <ArrowUpRight
                size={14}
                className="relative z-10 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
              <span className="absolute inset-0 bg-[#c9d3d8] translate-y-full group-hover:translate-y-0 transition-transform duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
